(function() {
  var MAPBOX_VERSION = 'v3.25.0';
  var MAPBOX_SCRIPT = 'https://api.mapbox.com/mapbox-gl-js/' + MAPBOX_VERSION + '/mapbox-gl.js';
  var MAPBOX_CSS = 'https://api.mapbox.com/mapbox-gl-js/' + MAPBOX_VERSION + '/mapbox-gl.css';
  var namespace = window.__vendingMachineLocator || {
    instances: new Map(),
    lifecycleBound: false
  };
  window.__vendingMachineLocator = namespace;
  var instances = namespace.instances;

  function escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, function(character) {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      }[character];
    });
  }

  function toNumber(value) {
    var number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  var AUSTRALIAN_REGION_ALIASES = [
    ['qld', 'queensland', 'brisbane'],
    ['nsw', 'new south wales', 'sydney'],
    ['vic', 'victoria', 'melbourne'],
    ['sa', 'south australia', 'adelaide'],
    ['wa', 'western australia', 'perth'],
    ['tas', 'tasmania', 'hobart'],
    ['nt', 'northern territory', 'darwin'],
    ['act', 'australian capital territory', 'canberra']
  ];

  function normalizeSearchText(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function containsSearchTerm(text, term) {
    return new RegExp('(^|\\s)' + term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '(\\s|$)').test(text);
  }

  function getAustralianRegionAliases(location) {
    var source = normalizeSearchText([
      location.title,
      location.address,
      location.excerpt,
      location.status,
      location.openingHours,
      location.accessInformation,
      location.machineId,
      Array.isArray(location.tags) ? location.tags.join(' ') : ''
    ].join(' '));
    var aliases = [];

    AUSTRALIAN_REGION_ALIASES.forEach(function(group) {
      var matches = group.some(function(alias) {
        return containsSearchTerm(source, normalizeSearchText(alias));
      });

      if (matches) {
        aliases = aliases.concat(group);
      }
    });

    return aliases.join(' ');
  }

  function buildLocationSearchText(location) {
    return normalizeSearchText([
      location.title,
      location.address,
      location.excerpt,
      location.status,
      location.openingHours,
      location.accessInformation,
      location.machineId,
      Array.isArray(location.tags) ? location.tags.join(' ') : '',
      getAustralianRegionAliases(location)
    ].join(' '));
  }

  function isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function escapeSelector(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(value);
    }

    return String(value).replace(/["\\]/g, '\\$&');
  }

  function loadMapboxAssets() {
    if (window.mapboxgl) {
      return Promise.resolve(window.mapboxgl);
    }

    if (!window.__vendingMachineLocatorMapboxPromise) {
      if (!document.querySelector('link[data-vending-machine-mapbox-css]')) {
        var link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = MAPBOX_CSS;
        link.setAttribute('data-vending-machine-mapbox-css', '');
        document.head.appendChild(link);
      }

      window.__vendingMachineLocatorMapboxPromise = new Promise(function(resolve, reject) {
        var existing = document.querySelector('script[data-vending-machine-mapbox-js]');

        if (existing && existing.getAttribute('data-load-failed') === 'true') {
          existing.parentNode.removeChild(existing);
          existing = null;
        }

        if (existing) {
          existing.addEventListener('load', function() {
            resolve(window.mapboxgl);
          }, { once: true });
          existing.addEventListener('error', reject, { once: true });
          return;
        }

        var script = document.createElement('script');
        script.src = MAPBOX_SCRIPT;
        script.async = true;
        script.defer = true;
        script.setAttribute('data-vending-machine-mapbox-js', '');
        script.onload = function() {
          resolve(window.mapboxgl);
        };
        script.onerror = function(error) {
          script.setAttribute('data-load-failed', 'true');
          script.parentNode.removeChild(script);
          reject(error);
        };
        document.head.appendChild(script);
      }).catch(function(error) {
        window.__vendingMachineLocatorMapboxPromise = null;
        throw error;
      });
    }

    return window.__vendingMachineLocatorMapboxPromise;
  }

  function VendingMachineLocator(root) {
    this.root = root;
    this.id = root.getAttribute('data-section-id');
    this.settings = this.readSettings();
    this.refs = this.readRefs();
    this.locations = this.parseLocations();
    this.filteredLocations = this.locations.slice();
    this.markers = new Map();
    this.activeLocationId = null;
    this.map = null;
    this.popup = null;
    this.origin = null;
    this.geocodeAbortController = null;
    this.mapLoadObserver = null;
    this.mapLoading = false;
    this.mapRequested = false;
    this.mapReady = false;
    this.listeners = [];
    this.localSearchTimer = null;
  }

  VendingMachineLocator.prototype.readSettings = function() {
    var data = this.root.dataset;
    return {
      token: data.mapboxToken || '',
      styleUrl: data.mapboxStyle || 'mapbox://styles/mapbox/standard',
      mapLoadingBehavior: data.mapLoadingBehavior === 'automatic' || data.mapLoadingBehavior === 'on_scroll' ? data.mapLoadingBehavior : 'on_click',
      country: (data.geocodingCountry || 'au').trim().toLowerCase(),
      defaultLatitude: toNumber(data.defaultLatitude) || -34.9285,
      defaultLongitude: toNumber(data.defaultLongitude) || 138.6007,
      defaultZoom: toNumber(data.defaultZoom) || 11,
      selectedZoom: toNumber(data.selectedZoom) || 14,
      fitLocations: data.fitLocations === 'true',
      navigationControls: data.navigationControls === 'true',
      fullscreenControl: data.fullscreenControl === 'true',
      geographicSearch: data.geographicSearch === 'true',
      useLocation: data.useLocation === 'true',
      showImages: data.showImages === 'true',
      showExcerpt: data.showExcerpt === 'true',
      showAddress: data.showAddress === 'true',
      showStatus: data.showStatus === 'true',
      showOpeningHours: data.showOpeningHours === 'true',
      showGallery: data.showGallery === 'true',
      showViewLocation: data.showViewLocation === 'true',
      showLocationList: data.showLocationList === 'true',
      openDirectionsNewTab: data.openDirectionsNewTab === 'true',
      maxResults: Math.max(parseInt(data.maxResults || '24', 10), 1),
      searchRadius: toNumber(data.searchRadius) || 0,
      distanceUnit: data.distanceUnit === 'miles' ? 'miles' : 'kilometres',
      directionsLabel: data.directionsLabel || 'Get Directions',
      locationLabel: data.locationLabel || 'View Location',
      noResultsMessage: data.noResultsMessage || 'No locations match your search.',
      emptyMessage: data.emptyMessage || 'No vending machine locations are available right now.',
      mapUnavailableMessage: data.mapUnavailableMessage || 'Map is currently unavailable. Browse the location list instead.',
      loadMapLabel: data.loadMapLabel || 'Load Map',
      mapLoadingMessage: data.mapLoadingMessage || 'Loading map...',
      mapLoadedMessage: data.mapLoadedMessage || 'Map loaded.',
      mapPreviewText: data.mapPreviewText || 'Load the interactive map to search by distance and explore nearby machines.',
      geocodeErrorMessage: data.geocodeErrorMessage || 'We could not find that place. Try a suburb, postcode or address.',
      geolocationErrorMessage: data.geolocationErrorMessage || 'Your location could not be used. Search by suburb or postcode instead.',
      searchingMessage: data.searchingMessage || 'Searching...',
      geolocationLoadingMessage: data.geolocationLoadingMessage || 'Getting your location...',
      searchSuccessMessage: data.searchSuccessMessage || 'Locations sorted by distance.',
      openingHoursLabel: data.openingHoursLabel || 'Opening hours',
      accessLabel: data.accessLabel || 'Access',
      markerImage: data.markerImage || '',
      activeMarkerImage: data.activeMarkerImage || ''
    };
  };

  VendingMachineLocator.prototype.readRefs = function() {
    return {
      data: this.root.querySelector('[data-location-data]'),
      form: this.root.querySelector('[data-locator-search-form]'),
      input: this.root.querySelector('[data-locator-search-input]'),
      resets: this.root.querySelectorAll('[data-locator-reset]'),
      locate: this.root.querySelector('[data-locator-geolocation]'),
      feedback: this.root.querySelector('[data-locator-feedback]'),
      results: this.root.querySelector('[data-locator-results]'),
      count: this.root.querySelector('[data-locator-count]'),
      map: this.root.querySelector('[data-locator-map]'),
      mapFallback: this.root.querySelector('[data-locator-map-fallback]'),
      placeholder: this.root.querySelector('[data-locator-placeholder]'),
      loadMap: this.root.querySelector('[data-locator-load-map]'),
      detail: this.root.querySelector('[data-locator-detail]'),
      tabs: this.root.querySelectorAll('[data-locator-tab]')
    };
  };

  VendingMachineLocator.prototype.parseLocations = function() {
    if (!this.refs.data) {
      return [];
    }

    try {
      var parsed = JSON.parse(this.refs.data.textContent || '[]');
      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed.map(function(location) {
        var latitude = toNumber(location.latitude);
        var longitude = toNumber(location.longitude);

        if (latitude === null || longitude === null || latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
          return null;
        }

        return Object.assign({}, location, {
          id: String(location.id),
          latitude: latitude,
          longitude: longitude,
          searchText: buildLocationSearchText(location)
        });
      }).filter(Boolean);
    } catch (error) {
      this.showMessage(this.settings.emptyMessage, true);
      return [];
    }
  };

  VendingMachineLocator.prototype.init = function() {
    this.bindEvents();
    this.renderResults(this.filteredLocations);

    if (!this.locations.length) {
      this.renderLocationDetails(null);
      return;
    }

    if (!this.settings.token) {
      this.root.classList.add('is-token-missing');
      this.showMapFallback();
      return;
    }

    this.prepareMapLoading();
  };

  VendingMachineLocator.prototype.bindEvents = function() {
    var self = this;

    if (this.refs.form) {
      this.addListener(this.refs.form, 'submit', function(event) {
        event.preventDefault();
        self.geocodeQuery();
      });
    }

    if (this.refs.input) {
      this.addListener(this.refs.input, 'input', function() {
        clearTimeout(self.localSearchTimer);
        self.localSearchTimer = window.setTimeout(function() {
          self.filterLocalResults();
        }, 180);
      });
    }

    this.refs.resets.forEach(function(reset) {
      self.addListener(reset, 'click', function() {
        self.resetSearch();
      });
    });

    if (this.refs.locate) {
      this.addListener(this.refs.locate, 'click', function() {
        self.useCurrentLocation();
      });
    }

    if (this.refs.loadMap) {
      this.addListener(this.refs.loadMap, 'click', function() {
        self.requestMapboxLoad();
      });
    }

    if (this.refs.results) {
      this.addListener(this.refs.results, 'click', function(event) {
        var copyAddress = event.target.closest('[data-copy-address]');
        if (copyAddress && self.root.contains(copyAddress)) {
          event.preventDefault();
          event.stopPropagation();
          self.copyAddress(copyAddress);
          return;
        }

        var cardButton = event.target.closest('[data-location-card]');
        if (!cardButton || !self.root.contains(cardButton)) {
          return;
        }

        self.selectLocation(cardButton.getAttribute('data-location-card'), {
          moveMap: true,
          scroll: false,
          fromList: true
        });
      });
    }

    if (this.refs.detail) {
      this.addListener(this.refs.detail, 'click', function(event) {
        event.stopPropagation();
        var videoPlayer = event.target.closest('[data-location-video-player]');
        var copyAddress = event.target.closest('[data-copy-address]');
        var playButton = event.target.closest('[data-location-video-play]');
        var closeButton = event.target.closest('[data-location-video-close]');
        var detailCloseButton = event.target.closest('[data-location-detail-close]');

        if (videoPlayer) {
          event.preventDefault();
          self.toggleLocationVideo(videoPlayer);
          return;
        }

        if (copyAddress) {
          event.preventDefault();
          self.copyAddress(copyAddress);
          return;
        }

        if (playButton) {
          event.preventDefault();
          self.playLocationVideo(playButton.getAttribute('data-location-video-play'));
          return;
        }

        if (closeButton) {
          event.preventDefault();
          self.closeLocationVideo();
          return;
        }

        if (detailCloseButton) {
          event.preventDefault();
          self.closeLocationOverlay();
        }
      });

      this.addListener(this.refs.detail, 'keydown', function(event) {
        var copyAddress = event.target.closest('[data-copy-address]');
        if (!copyAddress || (event.key !== 'Enter' && event.key !== ' ')) {
          return;
        }

        event.preventDefault();
        self.copyAddress(copyAddress);
      });
    }

    this.refs.tabs.forEach(function(tab) {
      self.addListener(tab, 'click', function() {
        self.setMobileView(tab.getAttribute('data-locator-tab'));
      });
    });
  };

  VendingMachineLocator.prototype.addListener = function(element, eventName, handler) {
    element.addEventListener(eventName, handler);
    this.listeners.push({ element: element, eventName: eventName, handler: handler });
  };

  VendingMachineLocator.prototype.prepareMapLoading = function() {
    this.root.classList.add('is-map-deferred');

    if (this.settings.mapLoadingBehavior === 'automatic') {
      this.requestMapboxLoad();
      return;
    }

    if (this.settings.mapLoadingBehavior === 'on_scroll') {
      this.observeMapForLoading();
    }
  };

  VendingMachineLocator.prototype.observeMapForLoading = function() {
    var self = this;
    var target = this.refs.map || this.root;

    if (!('IntersectionObserver' in window)) {
      this.requestMapboxLoad();
      return;
    }

    this.mapLoadObserver = new IntersectionObserver(function(entries) {
      var entry = entries[0];
      if (entry && entry.isIntersecting) {
        self.requestMapboxLoad();
      }
    }, {
      rootMargin: '180px 0px'
    });

    this.mapLoadObserver.observe(target);
  };

  VendingMachineLocator.prototype.requestMapboxLoad = function() {
    if (this.map || this.mapLoading) {
      return;
    }

    if (!this.settings.token) {
      this.root.classList.add('is-token-missing');
      this.showMapFallback();
      this.restoreLoadButton();
      return;
    }

    this.mapRequested = true;
    this.mapLoading = true;
    this.root.classList.add('is-map-loading');
    this.root.classList.remove('is-map-unavailable');
    this.setLoadButtonLoading();
    this.showMessage(this.settings.mapLoadingMessage);

    if (this.mapLoadObserver) {
      this.mapLoadObserver.disconnect();
      this.mapLoadObserver = null;
    }

    this.loadMapbox();
  };

  VendingMachineLocator.prototype.loadMapbox = function() {
    var self = this;

    loadMapboxAssets()
      .then(function(mapboxgl) {
        if (!self.root.isConnected) {
          return;
        }

        if (!mapboxgl || !mapboxgl.supported()) {
          self.showMapUnavailable();
          return;
        }

        self.initializeMap(mapboxgl);
      })
      .catch(function() {
        self.showMapUnavailable();
      });
  };

  VendingMachineLocator.prototype.initializeMap = function(mapboxgl) {
    if (!this.refs.map || this.map) {
      return;
    }

    mapboxgl.accessToken = this.settings.token;

    try {
      this.map = new mapboxgl.Map({
        container: this.refs.map,
        style: this.settings.styleUrl,
        center: [this.settings.defaultLongitude, this.settings.defaultLatitude],
        zoom: this.settings.defaultZoom
      });

      if (this.settings.navigationControls) {
        this.map.addControl(new mapboxgl.NavigationControl(), 'top-right');
      }

      if (this.settings.fullscreenControl) {
        this.map.addControl(new mapboxgl.FullscreenControl(), 'top-right');
      }

      this.map.once('load', this.onMapLoad.bind(this, mapboxgl));
      this.map.on('error', this.onMapError.bind(this));
      this.map.on('click', this.closeLocationOverlay.bind(this));
    } catch (error) {
      this.showMapUnavailable();
    }
  };

  VendingMachineLocator.prototype.onMapLoad = function(mapboxgl) {
    this.mapLoading = false;
    this.mapReady = true;
    this.root.classList.remove('is-map-loading', 'is-map-deferred');
    this.root.classList.add('is-map-loaded');
    this.showMessage(this.settings.mapLoadedMessage);
    this.restoreLoadButton();
    this.createMarkers(mapboxgl);

    if (this.settings.fitLocations) {
      this.fitLocations();
    } else if (this.activeLocationId) {
      this.moveMapTo(this.getLocation(this.activeLocationId), false);
    }
  };

  VendingMachineLocator.prototype.onMapError = function(event) {
    if (event && event.error) {
      this.showMapUnavailable();
    }
  };

  VendingMachineLocator.prototype.createMarkers = function(mapboxgl) {
    var self = this;

    this.markers.forEach(function(marker) {
      marker.remove();
    });
    this.markers.clear();

    this.locations.forEach(function(location) {
      var markerElement = document.createElement('button');
      markerElement.type = 'button';
      markerElement.className = 'vending-machine-locator__marker';
      markerElement.setAttribute('aria-label', location.title);

      var image = location.id === self.activeLocationId && self.settings.activeMarkerImage
        ? self.settings.activeMarkerImage
        : self.settings.markerImage;

      if (image) {
        markerElement.classList.add('vending-machine-locator__marker-image');
        markerElement.style.backgroundImage = 'url("' + image.replace(/"/g, '%22') + '")';
      }

      markerElement.addEventListener('click', function(event) {
        event.stopPropagation();
        self.selectLocation(location.id, { moveMap: true, scroll: true });
      });

      var marker = new mapboxgl.Marker({ element: markerElement, anchor: 'bottom' })
        .setLngLat([location.longitude, location.latitude])
        .addTo(self.map);

      self.markers.set(location.id, { marker: marker, element: markerElement });
    });

    this.updateMarkerStates();
  };

  VendingMachineLocator.prototype.selectLocation = function(locationId, options) {
    var location = this.getLocation(locationId);
    if (!location) {
      return;
    }

    options = Object.assign({ moveMap: true, scroll: true, fromList: false, toggle: true }, options || {});

    if (options.toggle && this.activeLocationId === location.id && this.refs.detail && !this.refs.detail.hidden) {
      this.closeLocationOverlay();
      return;
    }

    this.activeLocationId = location.id;
    this.updateResultStates();
    this.updateMarkerStates();
    this.renderLocationDetails(location);

    if (this.map && options.moveMap) {
      this.moveMapTo(location, true);
    }

    if (options.scroll) {
      this.scrollResultIntoView(location.id);
    }

    if (options.fromList && isMobile()) {
      this.setMobileView('map');
    }
  };

  VendingMachineLocator.prototype.moveMapTo = function(location, animated) {
    if (!this.map || !location) {
      return;
    }

    this.map.easeTo({
      center: [location.longitude, location.latitude],
      zoom: Math.max(this.map.getZoom(), this.settings.selectedZoom),
      duration: animated && !prefersReducedMotion() ? 650 : 0
    });
  };

  VendingMachineLocator.prototype.fitLocations = function() {
    if (!this.map || !this.locations.length) {
      return;
    }

    if (this.locations.length === 1) {
      this.moveMapTo(this.locations[0], false);
      return;
    }

    var bounds = new window.mapboxgl.LngLatBounds();
    this.locations.forEach(function(location) {
      bounds.extend([location.longitude, location.latitude]);
    });
    this.map.fitBounds(bounds, {
      padding: isMobile() ? 44 : { top: 60, right: 80, bottom: 120, left: 80 },
      maxZoom: this.settings.selectedZoom,
      duration: prefersReducedMotion() ? 0 : 600
    });
  };

  VendingMachineLocator.prototype.renderResults = function(locations) {
    if (!this.refs.results || !this.settings.showLocationList) {
      return;
    }

    var visibleLocations = locations.slice(0, this.settings.maxResults);
    this.refs.results.innerHTML = visibleLocations.map(this.renderResultCard.bind(this)).join('');

    if (this.refs.count) {
      this.refs.count.textContent = String(visibleLocations.length);
    }

    if (!visibleLocations.length) {
      this.refs.results.innerHTML = '<p class="vending-machine-locator__empty">' + escapeHtml(this.settings.noResultsMessage) + '</p>';
    }

    this.updateResultStates();
  };

  VendingMachineLocator.prototype.renderResultCard = function(location) {
    var image = this.settings.showImages && location.featuredImage
      ? '<span class="vending-machine-locator__card-image"><img src="' + escapeHtml(location.featuredImage) + '" alt="' + escapeHtml(location.featuredImageAlt || location.title) + '" loading="lazy"></span>'
      : '';
    var status = this.settings.showStatus && location.status
      ? '<span class="vending-machine-locator__status">' + escapeHtml(location.status) + '</span>'
      : '';
    var address = this.settings.showAddress && location.address
      ? '<span class="vending-machine-locator__card-address reverse-text-underline-parent" data-copy-address="' + escapeHtml(location.address) + '" aria-label="Copy address"><i class="sky-icon icon-vending-selector fa-solid fa-location-dot" aria-hidden="true"></i><span class="vending-machine-locator__address-text reverse-text-underline-effect">' + escapeHtml(location.address) + '</span><span class="vending-machine-locator__copy-icon" aria-hidden="true"><i class="sky-icon icon-vending-selector fa-solid fa-copy"></i></span></span>'
      : '';
    var excerpt = this.settings.showExcerpt && location.excerpt
      ? '<span class="vending-machine-locator__card-excerpt">' + escapeHtml(location.excerpt) + '</span>'
      : '';
    var distance = location.distanceText
      ? '<span class="vending-machine-locator__distance" data-distance>' + escapeHtml(location.distanceText) + '</span>'
      : '<span class="vending-machine-locator__distance" data-distance hidden></span>';
    var directionsTarget = this.settings.openDirectionsNewTab ? ' target="_blank" rel="noopener noreferrer"' : '';
    var viewLink = this.settings.showViewLocation && location.articleUrl
      ? '<a class="btn button-outline" href="' + escapeHtml(location.articleUrl) + '"><i class="sky-icon icon-vending-selector fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i>' + escapeHtml(this.settings.locationLabel) + '</a>'
      : '';

    return [
      '<article class="vending-machine-locator__card" data-location-card-wrapper="' + escapeHtml(location.id) + '">',
      '<button type="button" class="vending-machine-locator__card-button' + (image ? ' vending-machine-locator__card-button--with-image' : '') + '" data-location-card="' + escapeHtml(location.id) + '">',
      image,
      '<span class="vending-machine-locator__card-content">',
      status,
      '<span class="vending-machine-locator__card-title">' + escapeHtml(location.title) + '</span>',
      address,
      excerpt,
      distance,
      '</span>',
      '</button>',
      '<div class="vending-machine-locator__card-actions">',
      '<a class="btn button-solid" href="' + escapeHtml(this.getDirectionsUrl(location)) + '"' + directionsTarget + '><i class="sky-icon icon-vending-selector fa-solid fa-route" aria-hidden="true"></i>' + escapeHtml(this.settings.directionsLabel) + '</a>',
      viewLink,
      '</div>',
      '</article>'
    ].join('');
  };

  VendingMachineLocator.prototype.renderLocationDetails = function(location) {
    if (!this.refs.detail) {
      return;
    }

    if (!location) {
      this.refs.detail.hidden = true;
      this.refs.detail.innerHTML = '';
      return;
    }

    var status = this.settings.showStatus && location.status
      ? '<span class="vending-machine-locator__status">' + escapeHtml(location.status) + '</span>'
      : '';
    var video = this.getLocationVideo(location);
    var videoThumbnail = video
      ? '<button type="button" class="vending-machine-locator__video-thumb" data-location-video-play="' + escapeHtml(location.id) + '" aria-label="Play video for ' + escapeHtml(location.title) + '">' +
          '<img src="' + escapeHtml(video.poster || location.featuredImage || '') + '" alt="" loading="lazy">' +
          '<span class="vending-machine-locator__video-play" aria-hidden="true"><i class="sky-icon fa-solid fa-play"></i></span>' +
        '</button>'
      : '';
    var image = this.settings.showImages && location.featuredImage
      ? '<div class="vending-machine-locator__detail-image" data-location-video-frame>' +
          '<img src="' + escapeHtml(location.featuredImage) + '" alt="' + escapeHtml(location.featuredImageAlt || location.title) + '" loading="lazy">' +
          status +
          videoThumbnail +
        '</div>'
      : '';
    var bodyStatus = !image ? status : '';
    var address = this.settings.showAddress && location.address
      ? '<p class="vending-machine-locator__detail-text vending-machine-locator__detail-address reverse-text-underline-parent" data-copy-address="' + escapeHtml(location.address) + '" tabindex="0" role="button" aria-label="Copy address"><i class="sky-icon icon-vending-selector fa-solid fa-location-dot" aria-hidden="true"></i><span class="vending-machine-locator__address-text reverse-text-underline-effect">' + escapeHtml(location.address) + '</span><span class="vending-machine-locator__copy-icon" aria-hidden="true"><i class="sky-icon icon-vending-selector fa-solid fa-copy"></i></span></p>'
      : '';
    var excerpt = this.settings.showExcerpt && location.excerpt
      ? '<p class="vending-machine-locator__detail-text vending-machine-locator__detail-excerpt"><span class="vending-machine-locator__detail-excerpt-text">' + escapeHtml(location.excerpt) + '</span>' + (location.articleUrl ? '<a class="vending-machine-locator__read-more" href="' + escapeHtml(location.articleUrl) + '">Read more</a>' : '') + '</p>'
      : '';
    var hours = this.settings.showOpeningHours && location.openingHours
      ? '<p class="vending-machine-locator__hours"><span class="vending-machine-locator__label"><i class="sky-icon icon-vending-selector fa-solid fa-clock" aria-hidden="true"></i>' + escapeHtml(this.settings.openingHoursLabel) + '</span>' + escapeHtml(location.openingHours).replace(/\n/g, '<br>') + '</p>'
      : '';
    var access = location.accessInformation
      ? '<p class="vending-machine-locator__access"><span class="vending-machine-locator__label"><i class="sky-icon icon-vending-selector fa-solid fa-person-walking" aria-hidden="true"></i>' + escapeHtml(this.settings.accessLabel) + '</span>' + escapeHtml(location.accessInformation).replace(/\n/g, '<br>') + '</p>'
      : '';
    var gallery = this.renderGallery(location);
    var directionsTarget = this.settings.openDirectionsNewTab ? ' target="_blank" rel="noopener noreferrer"' : '';
    var viewLink = this.settings.showViewLocation && location.articleUrl
      ? '<a class="btn button-outline" href="' + escapeHtml(location.articleUrl) + '"><i class="sky-icon icon-vending-selector fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i>' + escapeHtml(this.settings.locationLabel) + '</a>'
      : '';

    this.refs.detail.hidden = false;
    this.refs.detail.innerHTML = [
      '<div class="vending-machine-locator__detail-scroll">',
      '<button type="button" class="vending-machine-locator__detail-close" data-location-detail-close aria-label="Close location details"><i class="sky-icon icon-vending-selector fa-solid fa-xmark" aria-hidden="true"></i></button>',
      image,
      '<div class="vending-machine-locator__detail-body">',
      bodyStatus,
      '<h3 class="vending-machine-locator__detail-title h4">' + escapeHtml(location.title) + '</h3>',
      address,
      excerpt,
      hours,
      access,
      gallery,
      '<div class="vending-machine-locator__detail-actions">',
      '<a class="btn button-solid" href="' + escapeHtml(this.getDirectionsUrl(location)) + '"' + directionsTarget + '><i class="sky-icon icon-vending-selector fa-solid fa-route" aria-hidden="true"></i>' + escapeHtml(this.settings.directionsLabel) + '</a>',
      viewLink,
      '</div>',
      '</div>',
      '</div>'
    ].join('');
  };

  VendingMachineLocator.prototype.getLocationVideo = function(location) {
    if (!location || !location.locationVideo) {
      return null;
    }

    if (typeof location.locationVideo === 'string') {
      return {
        url: location.locationVideo,
        type: '',
        poster: location.featuredImage || ''
      };
    }

    if (location.locationVideo.url) {
      return {
        url: location.locationVideo.url,
        type: location.locationVideo.type || '',
        poster: location.locationVideo.poster || location.featuredImage || ''
      };
    }

    return null;
  };

  VendingMachineLocator.prototype.playLocationVideo = function(locationId) {
    var location = this.getLocation(locationId);
    var video = this.getLocationVideo(location);
    var frame = this.refs.detail ? this.refs.detail.querySelector('[data-location-video-frame]') : null;

    if (!location || !video || !video.url || !frame) {
      return;
    }

    frame.innerHTML = [
      '<video class="vending-machine-locator__video" data-location-video-player controls autoplay playsinline preload="metadata"' + (video.poster ? ' poster="' + escapeHtml(video.poster) + '"' : '') + '>',
      '<source src="' + escapeHtml(video.url) + '"' + (video.type ? ' type="' + escapeHtml(video.type) + '"' : '') + '>',
      '</video>',
      '<button type="button" class="vending-machine-locator__video-close" data-location-video-close aria-label="Back to featured image"><i class="sky-icon icon-vending-selector fa-solid fa-arrow-left" aria-hidden="true"></i></button>'
    ].join('');
  };

  VendingMachineLocator.prototype.closeLocationVideo = function() {
    var activeLocation = this.getLocation(this.activeLocationId);
    var video = this.refs.detail ? this.refs.detail.querySelector('video') : null;

    if (video) {
      video.pause();
    }

    if (activeLocation) {
      this.renderLocationDetails(activeLocation);
    }
  };

  VendingMachineLocator.prototype.toggleLocationVideo = function(video) {
    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  };

  VendingMachineLocator.prototype.copyAddress = function(element) {
    var address = element.getAttribute('data-copy-address') || '';
    var self = this;

    if (!address) {
      return;
    }

    function done() {
      element.classList.add('is-copied');
      window.setTimeout(function() {
        element.classList.remove('is-copied');
      }, 1200);
      self.showMessage('Address copied to clipboard.');
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(address).then(done).catch(function() {
        self.copyAddressFallback(address);
        done();
      });
      return;
    }

    this.copyAddressFallback(address);
    done();
  };

  VendingMachineLocator.prototype.copyAddressFallback = function(address) {
    var input = document.createElement('textarea');
    input.value = address;
    input.setAttribute('readonly', '');
    input.style.position = 'fixed';
    input.style.top = '-1000px';
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);
  };

  VendingMachineLocator.prototype.renderGallery = function(location) {
    if (!this.settings.showGallery || !Array.isArray(location.gallery) || !location.gallery.length) {
      return '';
    }

    return '<div class="vending-machine-locator__gallery">' + location.gallery.map(function(image) {
      return '<div class="vending-machine-locator__gallery-item"><img src="' + escapeHtml(image.url) + '" alt="' + escapeHtml(image.alt || location.title) + '" loading="lazy"></div>';
    }).join('') + '</div>';
  };

  VendingMachineLocator.prototype.renderPopup = function(location) {
    var image = this.settings.showImages && location.featuredImage
      ? '<div class="vending-machine-locator__popup-image"><img src="' + escapeHtml(location.featuredImage) + '" alt="' + escapeHtml(location.featuredImageAlt || location.title) + '" loading="lazy"></div>'
      : '';
    var address = this.settings.showAddress && location.address
      ? '<p class="vending-machine-locator__popup-text">' + escapeHtml(location.address) + '</p>'
      : '';
    var status = this.settings.showStatus && location.status
      ? '<span class="vending-machine-locator__status">' + escapeHtml(location.status) + '</span>'
      : '';
    var directionsTarget = this.settings.openDirectionsNewTab ? ' target="_blank" rel="noopener noreferrer"' : '';

    return [
      '<div class="vending-machine-locator__popup">',
      image,
      status,
      '<h3 class="vending-machine-locator__popup-title">' + escapeHtml(location.title) + '</h3>',
      address,
      '<a class="btn button-solid" href="' + escapeHtml(this.getDirectionsUrl(location)) + '"' + directionsTarget + '><i class="sky-icon icon-vending-selector fa-solid fa-route" aria-hidden="true"></i>' + escapeHtml(this.settings.directionsLabel) + '</a>',
      '</div>'
    ].join('');
  };

  VendingMachineLocator.prototype.closeLocationOverlay = function() {
    if (this.popup) {
      this.popup.remove();
    }

    this.activeLocationId = null;
    this.renderLocationDetails(null);
    this.updateResultStates();
    this.updateMarkerStates();
  };

  VendingMachineLocator.prototype.filterLocalResults = function() {
    var query = this.refs.input ? normalizeSearchText(this.refs.input.value) : '';
    var source = this.origin ? this.sortByDistance(this.locations.slice(), this.origin) : this.locations.slice();

    if (query) {
      source = source.filter(function(location) {
        return location.searchText.indexOf(query) !== -1;
      });
    }

    this.filteredLocations = this.applyRadius(source);
    this.renderResults(this.filteredLocations);

    if (this.filteredLocations.length) {
      this.selectLocation(this.filteredLocations[0].id, { moveMap: true, scroll: false });
      this.showMessage('');
    } else {
      this.renderLocationDetails(null);
      this.showMessage(this.settings.noResultsMessage, true);
    }
  };

  VendingMachineLocator.prototype.geocodeQuery = function() {
    var self = this;
    var query = this.refs.input ? this.refs.input.value.trim() : '';

    if (!query) {
      this.filterLocalResults();
      return;
    }

    if (!this.settings.geographicSearch || !this.settings.token) {
      this.filterLocalResults();
      return;
    }

    if (!this.mapReady) {
      this.filterLocalResults();
      this.showMessage(this.settings.mapLoadingBehavior === 'on_click' ? this.settings.mapPreviewText || this.settings.mapUnavailableMessage : this.settings.mapLoadingMessage, true);
      return;
    }

    if (this.geocodeAbortController) {
      this.geocodeAbortController.abort();
    }

    this.geocodeAbortController = new AbortController();
    this.showMessage(this.settings.searchingMessage);

    var country = this.settings.country ? '&country=' + encodeURIComponent(this.settings.country) : '';
    var endpoint = 'https://api.mapbox.com/geocoding/v5/mapbox.places/' + encodeURIComponent(query) + '.json?limit=1&types=address,place,postcode,locality,neighborhood' + country + '&access_token=' + encodeURIComponent(this.settings.token);

    fetch(endpoint, { signal: this.geocodeAbortController.signal })
      .then(function(response) {
        if (!response.ok) {
          throw new Error('Mapbox geocoding failed');
        }
        return response.json();
      })
      .then(function(payload) {
        var feature = payload && payload.features && payload.features[0];
        if (!feature || !Array.isArray(feature.center)) {
          throw new Error('No geocoding results');
        }

        self.origin = {
          longitude: Number(feature.center[0]),
          latitude: Number(feature.center[1])
        };
        self.filteredLocations = self.applyRadius(self.sortByDistance(self.locations.slice(), self.origin));
        self.renderResults(self.filteredLocations);

        if (self.map) {
          self.map.easeTo({
            center: [self.origin.longitude, self.origin.latitude],
            zoom: Math.max(self.settings.defaultZoom, 11),
            duration: prefersReducedMotion() ? 0 : 550
          });
        }

        if (self.filteredLocations.length) {
          self.selectLocation(self.filteredLocations[0].id, { moveMap: true, scroll: true });
          self.showMessage(self.settings.searchSuccessMessage);
        } else {
          self.renderLocationDetails(null);
          self.showMessage(self.settings.noResultsMessage, true);
        }
      })
      .catch(function(error) {
        if (error.name === 'AbortError') {
          return;
        }
        self.showMessage(self.settings.geocodeErrorMessage, true);
      });
  };

  VendingMachineLocator.prototype.useCurrentLocation = function() {
    var self = this;

    if (!navigator.geolocation) {
      this.showMessage(this.settings.geolocationErrorMessage, true);
      return;
    }

    if (this.refs.locate) {
      this.refs.locate.disabled = true;
      this.refs.locate.setAttribute('aria-busy', 'true');
    }

    this.showMessage(this.settings.geolocationLoadingMessage);

    navigator.geolocation.getCurrentPosition(function(position) {
      self.origin = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude
      };
      self.filteredLocations = self.applyRadius(self.sortByDistance(self.locations.slice(), self.origin));
      self.renderResults(self.filteredLocations);

      if (self.filteredLocations.length) {
        self.selectLocation(self.filteredLocations[0].id, { moveMap: true, scroll: true });
        self.showMessage(self.settings.searchSuccessMessage);
      } else {
        self.showMessage(self.settings.noResultsMessage, true);
      }

      if (self.refs.locate) {
        self.refs.locate.disabled = false;
        self.refs.locate.removeAttribute('aria-busy');
      }
    }, function() {
      self.showMessage(self.settings.geolocationErrorMessage, true);
      if (self.refs.locate) {
        self.refs.locate.disabled = false;
        self.refs.locate.removeAttribute('aria-busy');
      }
    }, {
      enableHighAccuracy: false,
      timeout: 10000,
      maximumAge: 300000
    });
  };

  VendingMachineLocator.prototype.applyRadius = function(locations) {
    if (!this.origin || !this.settings.searchRadius) {
      return locations;
    }

    return locations.filter(function(location) {
      return location.distanceValue <= this.settings.searchRadius;
    }, this);
  };

  VendingMachineLocator.prototype.sortByDistance = function(locations, origin) {
    var self = this;

    return locations.map(function(location) {
      var kilometers = self.calculateDistance(origin.latitude, origin.longitude, location.latitude, location.longitude);
      var distanceValue = self.settings.distanceUnit === 'miles' ? kilometers * 0.621371 : kilometers;

      return Object.assign({}, location, {
        distanceValue: distanceValue,
        distanceText: self.formatDistance(distanceValue)
      });
    }).sort(function(a, b) {
      return a.distanceValue - b.distanceValue;
    });
  };

  VendingMachineLocator.prototype.calculateDistance = function(lat1, lon1, lat2, lon2) {
    var earthRadiusKm = 6371;
    var dLat = this.toRadians(lat2 - lat1);
    var dLon = this.toRadians(lon2 - lon1);
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return earthRadiusKm * c;
  };

  VendingMachineLocator.prototype.toRadians = function(degrees) {
    return degrees * Math.PI / 180;
  };

  VendingMachineLocator.prototype.formatDistance = function(value) {
    var rounded = value < 10 ? value.toFixed(1) : Math.round(value).toString();
    return rounded + ' ' + (this.settings.distanceUnit === 'miles' ? 'mi' : 'km');
  };

  VendingMachineLocator.prototype.resetSearch = function() {
    if (this.refs.input) {
      this.refs.input.value = '';
    }

    this.origin = null;
    this.filteredLocations = this.locations.slice();
    this.renderResults(this.filteredLocations);
    this.showMessage('');

    if (this.filteredLocations.length) {
      this.selectLocation(this.filteredLocations[0].id, { moveMap: true, scroll: false });
    }

    if (this.map && this.settings.fitLocations) {
      this.fitLocations();
    }
  };

  VendingMachineLocator.prototype.setMobileView = function(view) {
    var nextView = view === 'list' ? 'list' : 'map';
    this.root.setAttribute('data-mobile-view', nextView);

    this.refs.tabs.forEach(function(tab) {
      var selected = tab.getAttribute('data-locator-tab') === nextView;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
    });

    if (nextView === 'map' && this.map) {
      var self = this;
      window.setTimeout(function() {
        self.map.resize();
      }, 60);
    }
  };

  VendingMachineLocator.prototype.updateResultStates = function() {
    var self = this;

    this.root.querySelectorAll('[data-location-card-wrapper]').forEach(function(card) {
      var active = card.getAttribute('data-location-card-wrapper') === self.activeLocationId;
      card.classList.toggle('is-active', active);
      var button = card.querySelector('[data-location-card]');
      if (button) {
        button.setAttribute('aria-current', active ? 'true' : 'false');
      }
    });
  };

  VendingMachineLocator.prototype.updateMarkerStates = function() {
    var self = this;

    this.markers.forEach(function(markerData, id) {
      var active = id === self.activeLocationId;
      markerData.element.classList.toggle('is-active', active);
      markerData.element.setAttribute('aria-pressed', active ? 'true' : 'false');

      if (self.settings.markerImage || self.settings.activeMarkerImage) {
        var image = active && self.settings.activeMarkerImage ? self.settings.activeMarkerImage : self.settings.markerImage;
        markerData.element.style.backgroundImage = image ? 'url("' + image.replace(/"/g, '%22') + '")' : '';
      }
    });
  };

  VendingMachineLocator.prototype.scrollResultIntoView = function(locationId) {
    var card = this.root.querySelector('[data-location-card-wrapper="' + escapeSelector(locationId) + '"]');
    if (card) {
      card.scrollIntoView({ block: 'nearest', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }
  };

  VendingMachineLocator.prototype.getLocation = function(locationId) {
    return this.locations.find(function(location) {
      return location.id === String(locationId);
    });
  };

  VendingMachineLocator.prototype.getDirectionsUrl = function(location) {
    if (location.directionsUrl) {
      return location.directionsUrl;
    }

    return 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(location.latitude + ',' + location.longitude);
  };

  VendingMachineLocator.prototype.showMessage = function(message, isError) {
    if (!this.refs.feedback) {
      return;
    }

    this.refs.feedback.textContent = message || '';
    this.refs.feedback.classList.toggle('is-error', Boolean(isError));
  };

  VendingMachineLocator.prototype.showMapFallback = function() {
    if (this.refs.mapFallback) {
      this.refs.mapFallback.textContent = this.settings.mapUnavailableMessage;
    }
  };

  VendingMachineLocator.prototype.showMapUnavailable = function() {
    this.mapLoading = false;
    this.root.classList.add('is-map-unavailable');
    this.root.classList.remove('is-map-loading');
    this.showMapFallback();
    this.restoreLoadButton();
    this.showMessage(this.settings.mapUnavailableMessage, true);
  };

  VendingMachineLocator.prototype.setLoadButtonLoading = function() {
    if (!this.refs.loadMap) {
      return;
    }

    this.refs.loadMap.disabled = true;
    this.refs.loadMap.setAttribute('aria-busy', 'true');
    this.refs.loadMap.innerHTML = '<i class="sky-icon icon-vending-selector fa-solid fa-map-location-dot" aria-hidden="true"></i>' + escapeHtml(this.settings.mapLoadingMessage);
  };

  VendingMachineLocator.prototype.restoreLoadButton = function() {
    if (!this.refs.loadMap) {
      return;
    }

    this.refs.loadMap.disabled = false;
    this.refs.loadMap.removeAttribute('aria-busy');
    this.refs.loadMap.innerHTML = '<i class="sky-icon icon-vending-selector fa-solid fa-map-location-dot" aria-hidden="true"></i>' + escapeHtml(this.settings.loadMapLabel);
  };

  VendingMachineLocator.prototype.destroy = function() {
    clearTimeout(this.localSearchTimer);

    if (this.geocodeAbortController) {
      this.geocodeAbortController.abort();
    }

    if (this.mapLoadObserver) {
      this.mapLoadObserver.disconnect();
      this.mapLoadObserver = null;
    }

    this.listeners.forEach(function(listener) {
      listener.element.removeEventListener(listener.eventName, listener.handler);
    });
    this.listeners = [];

    if (this.popup) {
      this.popup.remove();
    }

    this.markers.forEach(function(markerData) {
      markerData.marker.remove();
    });
    this.markers.clear();

    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  };

  function initRoot(root) {
    if (!root || instances.has(root)) {
      return;
    }

    var instance = new VendingMachineLocator(root);
    instances.set(root, instance);
    instance.init();
  }

  function initAll(context) {
    (context || document).querySelectorAll('[data-vending-machine-locator]').forEach(initRoot);
  }

  function destroyBySection(sectionId) {
    instances.forEach(function(instance, root) {
      if (instance.id === sectionId || !root.isConnected) {
        instance.destroy();
        instances.delete(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initAll(document);
    }, { once: true });
  } else {
    initAll(document);
  }

  if (!namespace.lifecycleBound) {
    namespace.lifecycleBound = true;

    document.addEventListener('shopify:section:load', function(event) {
      initAll(event.target);
    });

    document.addEventListener('shopify:section:unload', function(event) {
      destroyBySection(event.detail.sectionId);
    });
  }
})();
